import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  Radio,
  RadioGroup,
  Stack,
  Typography,
  Link,
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useLocation } from 'react-router-dom';
import LoadingSpinner from '../../../support/LoadingSpinner';
import { Assessment, AssessmentData } from '../../../types/AssessmentTypes';
import { useAuthContext } from '../../../../hooks/useAuthContext';
import axios from '../../../../services/ApiService';
import toast from 'react-hot-toast';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

interface AssessmentFormProps {
  userType: 'BUSINESS' | 'AGENT' | 'COMPANY' | 'ADMIN';
}

function AssessmentForm({ userType }: AssessmentFormProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { trackerId } = location.state || {};
  const { user } = useAuthContext();
  const [loading, setLoading] = useState(false);
  const [assessment, setAssessment] = useState<Assessment | null>(null);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  
  const [userAssessmentData, setUserAssessmentData] = useState<AssessmentData>({
    trackerId: 0,
    assignmentId: 0,
    stepId: 0,
    assessmentId: 0,
    assessmentName: '',
    workflowName: '',
    userId: user?.id || 0,
    userType: userType,
    status: 'IN_PROGRESS',
    totalScore: 0,
    scoredPoints: 0,
    result: '',
    startDate: new Date().toISOString(),
    lastUpdated: new Date().toISOString(),
    completedDate: undefined,
    answers: []
  });

  useEffect(() => {
    if (trackerId) {
      fetchAssessmentData(trackerId);
    }
  }, [trackerId]);

  const fetchAssessmentData = async (trackerId: number) => {
    try {
      setLoading(true);
      const response = await axios.get(`/getAssessmentData/${trackerId}`);
      setUserAssessmentData(response.data);
      await fetchAssessment(response.data.assessmentId);
    } catch (error) {
      console.error('Error fetching assessment data.');
    } finally {
      setLoading(false);
    }
  };

  const fetchAssessment = async (assessmentId: number) => {
    try {
      setLoading(true);
      const response = await axios.post("/getAssessment", assessmentId);
      setAssessment(response.data);
    } catch (error) {
      console.error('Error fetching assessment.');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerChange = (questionId: number, selectedOptionId: number) => {
    if (!assessment) return;

    let score = 0;
    let found = false;
    
    assessment.subCategories.forEach(subCategory => {
      subCategory.questions.forEach(question => {
        if (question.id === questionId) {
          const option = question.options.find(opt => opt.id === selectedOptionId);
          if (option) {
            score = option.score;
            found = true;
          }
        }
      });
    });

    if (!found) return;

    setUserAssessmentData(prev => {
      const answers = prev.answers || [];
      const existingAnswer = answers.find(a => a.questionId === questionId);
      const filteredAnswers = answers.filter(a => a.questionId !== questionId);
      
      const newAnswers = [...filteredAnswers, {
        id: existingAnswer?.id,
        questionId,
        selectedOptionId,
        score
      }];

      const totalScoredPoints = newAnswers.reduce((sum, answer) => sum + answer.score, 0);

      return {
        ...prev,
        answers: newAnswers,
        scoredPoints: totalScoredPoints,
        lastUpdated: new Date().toISOString()
      };
    });

    if (errors[questionId]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[questionId];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    if (!assessment) return false;
    
    const newErrors: { [key: string]: string } = {};
    assessment.subCategories.forEach(subCategory => {
      subCategory.questions.forEach(question => {
        if (!(userAssessmentData?.answers?.some(a => a.questionId === question.id))) {
          newErrors[question.id] = 'Please select an answer for this question';
        }
      });
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!assessment || !user) return;

    setLoading(true);
    try {
      const payload = {
        ...userAssessmentData,
        status: 'IN_PROGRESS',
        lastUpdated: new Date().toISOString()
      };

      await axios.post("/saveAssessmentData", payload);
      toast.success('Assessment saved as draft');
      navigate(-1);
    } catch (error) {
      toast.error('Error saving assessment');
      console.error('Error saving assessment:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!assessment || !user) return;

    // Validate all questions are answered
    const newErrors: { [key: string]: string } = {};
    let unansweredQuestions = 0;
    
    assessment.subCategories.forEach(subCategory => {
      subCategory.questions.forEach(question => {
        if (!(userAssessmentData?.answers?.some(a => a.questionId === question.id))) {
          newErrors[question.id] = 'Please select an answer for this question';
          unansweredQuestions++;
        }
      });
    });

    setErrors(newErrors);

    if (unansweredQuestions > 0) {
      toast.error(`Please answer all questions (${unansweredQuestions} remaining)`);
      return;
    }

    setLoading(true);
    try {
      // First save the assessment data
      const payload = {
        ...userAssessmentData,
        status: 'IN_PROGRESS',
        lastUpdated: new Date().toISOString()
      };
      
      try {
        await axios.post("/saveAssessmentData", payload);
      } catch (error) {
        toast.error('Failed to save assessment data. Please try again.');
        console.error('Error saving assessment data:', error);
        setLoading(false);
        return; // Exit without submitting if save fails
      }

      // Proceed with submit only if save was successful
      await axios.post("/submitAssessmentData", { trackerId: userAssessmentData.trackerId });
      
      toast.success('Assessment submitted successfully');
      navigate(-1);
    } catch (error) {
      toast.error('Error submitting assessment');
      console.error('Error submitting assessment:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!assessment) return <LoadingSpinner loading={loading} />;

  return (
    <>
      <Helmet>
        <title>{`BVT ${userType} - Assessment`}</title>
      </Helmet>
      <Box sx={{ padding: 3 }}>
        <Card>
          <CardHeader
            title={assessment.name}
            subheader={`Total Score: ${assessment.totalScore}`}
            sx={{ backgroundColor: '#f5f5f5' }}
          />
          <Divider />
          <CardContent>
            <Stack spacing={3}>
              <Typography variant="body1">
                {assessment.description}
              </Typography>

              {assessment.subCategories.map((subCategory) => (
                <Card key={subCategory.id} sx={{ backgroundColor: '#f9f9f9' }}>
                  <CardHeader
                    title={subCategory.title}
                    sx={{ backgroundColor: '#f0f0f0' }}
                  />
                  <CardContent>
                    <Stack spacing={3}>
                      {subCategory.questions.map((question) => (
                        <Box key={question.id}>
                          <Typography variant="subtitle1" gutterBottom>
                            {question.text}
                          </Typography>
                          {question.helpTip && (
                            question.isLink ? (
                              <Link
                                href={question.helpTip}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ 
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                  mb: 1
                                }}
                              >
                                Learn More
                                <OpenInNewIcon sx={{ fontSize: 16 }} />
                              </Link>
                            ) : (
                              <Typography 
                                variant="caption" 
                                color="textSecondary" 
                                display="block" 
                                gutterBottom
                              >
                                Tip: {question.helpTip}
                              </Typography>
                            )
                          )}
                          <FormControl 
                            error={Boolean(errors[question.id])} 
                            sx={{ width: '100%' }}
                          >
                            <RadioGroup
                              value={userAssessmentData?.answers?.find(a => a.questionId === question.id)?.selectedOptionId || ''}
                              onChange={(e) => handleAnswerChange(question.id, Number(e.target.value))}
                            >
                              <Grid container spacing={2}>
                                {question.options.map((option) => (
                                  <Grid item xs={12} sm={6} key={option.id}>
                                    <FormControlLabel
                                      value={option.id}
                                      control={<Radio />}
                                      label={option.label}
                                      sx={{
                                        backgroundColor: 'white',
                                        padding: 1,
                                        borderRadius: 1,
                                        width: '100%',
                                        margin: 0,
                                        '&:hover': {
                                          backgroundColor: '#f5f5f5',
                                        },
                                      }}
                                    />
                                  </Grid>
                                ))}
                              </Grid>
                            </RadioGroup>
                            {errors[question.id] && (
                              <FormHelperText>{errors[question.id]}</FormHelperText>
                            )}
                          </FormControl>
                        </Box>
                      ))}
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          </CardContent>
          <Divider />
          <CardActions sx={{ justifyContent: 'flex-end', padding: 2, gap: 1 }}>
            <Button
              variant="contained"
              color="error"
              onClick={() => navigate(-1)}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
            >
              Save Draft
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
            >
              Submit
            </Button>
          </CardActions>
        </Card>
      </Box>
      <LoadingSpinner loading={loading} />
    </>
  );
}

export default AssessmentForm; 